import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { WebsiteFooter } from '@/components/WebsiteFooter';
import { company } from '@/lib/company';
import '../sign-up.css';
import '../signup-foundation.css';
import '../website-legal.css';

type LegalDocument = 'privacy' | 'terms' | 'cookies';

function Contact() {
  return <a href={`mailto:${company.email}`}>{company.email}</a>;
}

const documents: Record<
  LegalDocument,
  {
    title: string;
    introduction: string;
    sections: { title: string; body: ReactNode }[];
  }
> = {
  privacy: {
    title: 'Privacy Policy',
    introduction:
      'How we use personal information when you visit this website, contact us or request early access to Asset Hound.',
    sections: [
      {
        title: 'Who is responsible for your information',
        body: (
          <p>
            {company.name} is the controller of the personal information
            described here. You can contact us at <Contact /> or write to our
            registered office at {company.registeredOffice}.
          </p>
        ),
      },
      {
        title: 'Information you give us',
        body: (
          <>
            <p>
              The access-request form collects your first and last name, work
              email, company name, team size and the types of work your firm
              does. A phone number is optional. We also receive information you
              choose to include when you email us.
            </p>
            <p>
              Providing this information is voluntary. We need the required form
              answers to understand and respond to your request. Please do not
              send client records, case files or sensitive personal information
              through this form.
            </p>
          </>
        ),
      },
      {
        title: 'Why we use it',
        body: (
          <>
            <p>
              We use your details to review access requests, respond to
              enquiries and discuss whether Asset Hound may be relevant to your
              firm. Our lawful basis is our legitimate interest in handling
              business enquiries and managing early-access discussions. We also
              have a legitimate interest in keeping this website secure and
              functioning.
            </p>
            <p>
              Submitting this form does not subscribe you to marketing emails.
              Access requests are reviewed by people; this form does not make an
              automated decision about you with legal or similarly significant
              effects.
            </p>
          </>
        ),
      },
      {
        title: 'Providers and technical information',
        body: (
          <>
            <p>
              We use HubSpot to receive and store access requests, Railway to
              host the website and Microsoft 365 for business email. These
              providers process information needed to supply their services.
              Hosting requests may include your IP address, browser information
              and the page requested.
            </p>
            <p>
              The website loads its typeface from Google Fonts. Your browser
              contacts Google to retrieve the font, which shares technical
              request information with Google. See our{' '}
              <a href="/cookies">Cookies notice</a> for more about website
              storage and third-party requests.
            </p>
          </>
        ),
      },
      {
        title: 'International processing',
        body: (
          <p>
            Our providers operate internationally and may process information
            outside the UK. HubSpot and Railway describe their transfer
            arrangements, including standard contractual clauses and the UK
            Addendum where applicable, in their{' '}
            <a href="https://legal.hubspot.com/dpa">
              HubSpot data-processing agreement
            </a>{' '}
            and{' '}
            <a href="https://railway.com/legal/dpa">
              Railway data-processing addendum
            </a>
            . Contact us if you would like further information about the
            safeguards relevant to your information.
          </p>
        ),
      },
      {
        title: 'How long we keep your information',
        body: (
          <p>
            We keep enquiry and access-request information for as long as it is
            needed to manage the request and any ongoing business discussion. We
            decide this by considering whether the enquiry is active, whether we
            are arranging access, when we last had contact and any legal need to
            retain a record. You can contact us to ask for your details to be
            removed; we will consider the request and explain any reason
            information must be retained.
          </p>
        ),
      },
      {
        title: 'Your rights',
        body: (
          <>
            <p>
              Depending on the circumstances, you can ask to access or correct
              your information, request erasure, or restrict its use. Data
              portability applies where its legal conditions are met. Contact{' '}
              <Contact /> to make a request.
            </p>
            <p>
              <strong>
                You have the right to object to processing based on legitimate
                interests.
              </strong>{' '}
              Tell us about your situation so we can consider your objection.
            </p>
            <p>
              You can also complain to the UK Information Commissioner’s Office
              at{' '}
              <a href="https://ico.org.uk/make-a-complaint/">
                ico.org.uk/make-a-complaint
              </a>
              .
            </p>
          </>
        ),
      },
      {
        title: 'Changes and scope',
        body: (
          <p>
            This notice covers the public website and early-access enquiries.
            Any future platform service or pilot may require additional privacy
            information. We will update this page when the website’s use of
            information changes.
          </p>
        ),
      },
    ],
  },
  terms: {
    title: 'Terms & Conditions',
    introduction:
      'These terms cover the public Asset Hound website and requests for early access.',
    sections: [
      {
        title: 'About this website',
        body: (
          <p>
            This website is operated by {company.name}, registered in{' '}
            {company.jurisdiction} under company number {company.number}. Our
            registered office is {company.registeredOffice}. For questions about
            these terms, contact <Contact />.
          </p>
        ),
      },
      {
        title: 'Development and early access',
        body: (
          <>
            <p>
              Asset Hound is in development. The roadmap describes our current
              plans, which may change. Descriptions, demonstrations and
              estimates on this website are not a promise of a particular
              release date, feature or commercial outcome.
            </p>
            <p>
              Requesting access registers your interest. It does not create a
              platform account, guarantee an invitation or enter you into a paid
              subscription. Any pilot or platform access will be subject to
              separately agreed terms.
            </p>
          </>
        ),
      },
      {
        title: 'Information on the website',
        body: (
          <p>
            The website explains our product plans. Its content is general
            information, not legal, insolvency, financial or other professional
            advice. Do not rely on it as a substitute for professional judgement
            or advice about a particular case.
          </p>
        ),
      },
      {
        title: 'Using the website',
        body: (
          <p>
            Use this website lawfully. Do not attempt unauthorised access,
            interfere with its operation, introduce malicious software or submit
            information you are not entitled to share. When contacting us,
            provide accurate details and avoid confidential client or case
            information.
          </p>
        ),
      },
      {
        title: 'Content and links',
        body: (
          <p>
            Our website content and branding are protected by applicable
            intellectual-property rights. You may view and share links to the
            website for legitimate business purposes. Do not reproduce our
            branding or present yourself as associated with us without
            permission. External websites are operated by their own providers
            and have their own terms and privacy notices.
          </p>
        ),
      },
      {
        title: 'Availability and responsibility',
        body: (
          <p>
            We may update or temporarily suspend the website. We do not promise
            uninterrupted or error-free availability. Nothing in these terms
            excludes or limits liability for fraud, fraudulent
            misrepresentation, death or personal injury caused by negligence, or
            anything else that cannot lawfully be excluded or limited.
          </p>
        ),
      },
      {
        title: 'Privacy and applicable law',
        body: (
          <>
            <p>
              Our <a href="/privacy">Privacy Policy</a> explains how we handle
              information provided through this website.
            </p>
            <p>
              These terms are governed by the law of England and Wales. The
              courts of England and Wales have jurisdiction, subject to any
              mandatory rights you have under applicable law.
            </p>
          </>
        ),
      },
    ],
  },
  cookies: {
    title: 'Cookies',
    introduction:
      'Cookies, browser storage and third-party requests on this website.',
    sections: [
      {
        title: 'Our current website',
        body: (
          <p>
            The early-access website does not set optional analytics or
            advertising cookies. The access-request form does not read a HubSpot
            tracking cookie, send browser cookies with its submission or add a
            marketing subscription.
          </p>
        ),
      },
      {
        title: 'Requests to other services',
        body: (
          <p>
            Website pages are hosted by Railway, fonts load from Google Fonts
            and access requests are sent to HubSpot when you submit the form.
            Those requests share technical information needed to deliver the
            service, even where no cookie is used. The{' '}
            <a href="/privacy">Privacy Policy</a> explains these providers and
            the information involved.
          </p>
        ),
      },
      {
        title: 'Your browser and future changes',
        body: (
          <p>
            You can manage stored website data through your browser settings. If
            we introduce optional tracking technologies, we will update this
            notice and provide any choices required before using them. For
            questions, contact <Contact />.
          </p>
        ),
      },
    ],
  },
};

export function WebsiteLegalPage({ document }: { document: LegalDocument }) {
  const content = documents[document];
  useEffect(() => {
    const previous = window.document.title;
    window.document.title = `${content.title} | Asset Hound`;
    return () => {
      window.document.title = previous;
    };
  }, [content.title]);

  return (
    <div className="signup-page legal-page">
      <a href="#main-content" className="signup-skip">
        Skip to content
      </a>
      <header className="signup-container signup-header">
        <a href="/" className="signup-brand" aria-label="Asset Hound home">
          <img
            src="/brand/asset-hound-icon.png"
            className="signup-brand-mark"
            alt=""
            aria-hidden="true"
            draggable={false}
          />
          <span>asset hound</span>
        </a>
        <a href="/" className="legal-back">
          <ArrowLeft size={15} aria-hidden="true" /> Back to home
        </a>
      </header>
      <main
        id="main-content"
        className="signup-container legal-main"
        tabIndex={-1}
      >
        <div className="legal-intro">
          <p className="legal-updated">Last updated 19 October 2026</p>
          <h1>{content.title}</h1>
          <p>{content.introduction}</p>
        </div>
        <article className="legal-content" aria-label={content.title}>
          {content.sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {section.body}
            </section>
          ))}
        </article>
      </main>
      <WebsiteFooter />
    </div>
  );
}
